import { useEffect } from "react";
import type { Lang } from "./i18n.js";

/**
 * Head management for the SPA routes. The content site (blog + guides) is
 * server-rendered by the API and carries its own complete head; this hook covers
 * what is left in the SPA — chiefly the home page, which is the site's most
 * valuable indexable URL — plus sane titles for the panel and auth screens.
 *
 * index.html ships the Turkish home page's tags statically so a crawler that
 * never executes JavaScript still gets a correct document; this hook keeps them
 * in sync when the language or route changes.
 */

export interface SeoInput {
  title: string;
  description: string;
  /** Canonical path, e.g. "/" or "/en". */
  path: string;
  lang: Lang;
  /** The same document in the other language, when one exists. */
  altPath?: string | undefined;
  /** Panel and auth routes must never be indexed. */
  noindex?: boolean | undefined;
}

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo(input: SeoInput): void {
  const { title, description, path, lang, altPath, noindex } = input;
  useEffect(() => {
    const origin = window.location.origin;
    document.title = title;
    document.documentElement.lang = lang;
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    );
    upsertLink("canonical", `${origin}${path}`);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", `${origin}${path}`);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", lang === "tr" ? "tr_TR" : "en_GB");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (altPath) {
      upsertLink("alternate", `${origin}${path}`, lang);
      upsertLink("alternate", `${origin}${altPath}`, lang === "tr" ? "en" : "tr");
      upsertLink("alternate", `${origin}${lang === "tr" ? path : altPath}`, "x-default");
    }
  }, [title, description, path, lang, altPath, noindex]);
}
